import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <Nav $scrolled={scrolled}>
      <NavContainer>
        <LogoContainer>
          <Link to="/">
            <Logo>RP</Logo>
          </Link>
        </LogoContainer>

        <MenuIcon onClick={toggleMenu}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </MenuIcon>

        <NavMenu $isOpen={menuOpen}>
          <NavItem
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <NavLink onClick={() => setMenuOpen(false)} to="/">
              <span>01.</span> Home
            </NavLink>
          </NavItem>
          <NavItem
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <NavLink onClick={() => setMenuOpen(false)} to="/about">
              <span>02.</span> About
            </NavLink>
          </NavItem>
          <NavItem
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <NavLink onClick={() => setMenuOpen(false)} to="/projects">
              <span>03.</span> Projects
            </NavLink>
          </NavItem>
          <NavItem
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <NavLink onClick={() => setMenuOpen(false)} to="/blog">
              <span>04.</span> Blog
            </NavLink>
          </NavItem>
          <ThemeToggleWrapper
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <ThemeToggle />
          </ThemeToggleWrapper>
          <NavButton
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <ResumeButton href="/resume.pdf" target="_blank" rel="noopener noreferrer">
              Resume
            </ResumeButton>
          </NavButton>
        </NavMenu>
      </NavContainer>
    </Nav>
  );
};

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10;
  height: var(--nav-height);
  background-color: ${({ $scrolled }) => ($scrolled ? 'rgba(10, 25, 47, 0.95)' : 'transparent')};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(10px)' : 'none')};
  transition: var(--transition);
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 10px 30px -10px rgba(2, 12, 27, 0.7)' : 'none')};
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 50px;
  max-width: 1600px;
  height: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const LogoContainer = styled.div`
  z-index: 11;
`;

const Logo = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--accent);
  border: 2px solid var(--accent);
  border-radius: 5px;
  padding: 5px 10px;
`;

const MenuIcon = styled.div`
  display: none;
  color: var(--accent);
  font-size: 24px;
  cursor: pointer;
  z-index: 11;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 75%;
    max-width: 300px;
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    background-color: var(--secondary);
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: var(--transition);
    padding: 50px;
    box-shadow: -10px 0px 30px -15px rgba(2, 12, 27, 0.7);
  }
`;

const NavItem = styled(motion.li)`
  margin: 0 1.5rem;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-primary);
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 14px;
  text-decoration: none;
  padding: 10px;
  
  span {
    color: var(--accent);
    margin-right: 5px;
  }

  &:hover {
    color: var(--accent);
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 15px 0;
  }
`;

const NavButton = styled(motion.div)`
  margin-left: 1.5rem;

  @media (max-width: 768px) {
    margin: 20px 0 0 0;
    width: 100%;
  }
`;

const ResumeButton = styled.a`
  border: 1px solid var(--accent);
  border-radius: var(--border-radius);
  padding: 0.75rem 1rem;
  color: var(--accent);
  background-color: transparent;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 14px;
  text-decoration: none;
  transition: var(--transition);
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    display: block;
    text-align: center;
  }
`;

const ThemeToggleWrapper = styled(motion.div)`
  margin: 0 1.5rem;
  
  @media (max-width: 768px) {
    margin: 10px 0;
  }
`;

export default Navbar;